import { addressSchema, UnauthorizedError } from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';
import { getDoctorAttendingResponseSchema } from './use-get-doctor-attending';

export const getDoctorAttendingItemResponseSchema =
  getDoctorAttendingResponseSchema.merge(
    z.object({
      office: z.object({
        id: z.string(),
        name: z.string(),
        address: addressSchema,
      }),
    }),
  );
export const getDoctorAttendingsResponseSchema = z.object({
  data: z.array(getDoctorAttendingItemResponseSchema),
});

export type GetDoctorAttendingItemResponse = z.infer<
  typeof getDoctorAttendingItemResponseSchema
>;
export type GetDoctorAttendingsResponse = z.infer<
  typeof getDoctorAttendingsResponseSchema
>;

export type GetOfficeDoctorAttendingsParams = {
  page: number;
  per_page: number;
  officeId?: string;
  search?: string;
};

const getOfficeDoctorAttendings =
  (params: GetOfficeDoctorAttendingsParams) => async () => {
    return http.doctorAttendings
      .getList(params)
      .then<GetDoctorAttendingsResponse>(({ response, transform }) => {
        return transform.handle({
          response,
          attributes: {
            userId: true,
            firstname: true,
            lastname: true,
            phoneNumber: true,
            orderRegistrationNumber: true,
            rhythm: true,
            onSiteSlotsWithoutAssistant: true,
            remoteSlotsWithoutAssistant: true,
            onSiteSlotsWithAssistant: true,
            remoteSlotsWithAssistant: true,
            rhythmNote: true,
            doctolibId: true,
            iban: true,
            bic: true,
            commissionRate: true,
            signatureUrl: true,
            workSchedulesDateRef: true,
            workSchedules: true,
            createdAt: true,
            activatedAt: true,
            deactivatedAt: true,
          },
          relations: {
            office: true,
          },
          schema: getDoctorAttendingsResponseSchema,
        }) as GetDoctorAttendingsResponse;
      });
  };

export const useGetOfficeDoctorAttendingsQuery = (
  params: GetOfficeDoctorAttendingsParams & { officeRequired: boolean },
): UseQueryResult<GetDoctorAttendingsResponse, UnauthorizedError> => {
  return useQuery({
    queryFn: getOfficeDoctorAttendings(params),
    queryKey: [
      'office-doctor-attendings',
      params.officeId,
      params.page,
      params.per_page,
      params.search,
    ],
    retry: false,
    enabled: params.officeRequired ? !!params.officeId : true,
  });
};
